import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  AlertCircle,
  CheckCircle2,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgentReviewConfig = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { agentId } = useParams();
  
  const [agent, setAgent] = useState(null);
  const [reviewAgents, setReviewAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchData();
  }, [user, navigate, agentId]);

  const fetchData = async () => {
    try {
      const [agentRes, reviewAgentsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/agents/${agentId}`),
        axios.get(`${API_URL}/api/admin/review-agents`),
        axios.get(`${API_URL}/api/admin/review-agents/agent/${agentId}/review-config`)
      ]);
      
      setAgent(agentRes.data);
      setReviewAgents(reviewAgentsRes.data);
      
      const config = configRes.data.review_config;
      setReviewEnabled(config?.review_enabled || false);
      setCheckpoints(config?.review_checkpoints || []);
      
    } catch (error) {
      console.error('獲取數據失敗:', error);
      toast.error('獲取數據失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate checkpoints
    if (reviewEnabled && checkpoints.length === 0) {
      toast.error('請至少添加一個審核點');
      return;
    }

    for (const cp of checkpoints) {
      if (!cp.name || !cp.review_agent_id || !cp.trigger_keyword || !cp.pass_keyword || !cp.fail_keyword) {
        toast.error('請填寫所有審核點的必填欄位');
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/review-agents/agent/${agentId}/review-config`, {
        review_enabled: reviewEnabled,
        review_checkpoints: checkpoints
      });
      toast.success('審核配置已儲存！');
      navigate('/admin/agents');
    } catch (error) {
      console.error('儲存失敗:', error);
      toast.error(error.response?.data?.detail || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const addCheckpoint = () => {
    if (checkpoints.length >= 5) {
      toast.error('最多只能添加 5 個審核點');
      return;
    }

    setCheckpoints([
      ...checkpoints,
      {
        id: `cp_${Date.now()}`,
        name: '',
        review_agent_id: '',
        trigger_keyword: '[送審]',
        pass_keyword: '[通過]',
        fail_keyword: '[不通過]',
        on_fail_action: 'ask_user'
      }
    ]);
  };

  const removeCheckpoint = (index) => {
    const newCheckpoints = [...checkpoints];
    newCheckpoints.splice(index, 1);
    setCheckpoints(newCheckpoints);
  };

  const updateCheckpoint = (index, field, value) => {
    const newCheckpoints = [...checkpoints];
    newCheckpoints[index][field] = value;
    setCheckpoints(newCheckpoints);
  };

  if (user?.role !== 'admin') return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-neutral-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-neutral-50 to-secondary-50 overflow-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-100 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/agents')}
              className="rounded-full"
              data-testid="back-to-agents-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回 Agent 列表
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-bold text-neutral-900">審核設定</h1>
                <p className="text-sm text-neutral-500">{agent?.name}</p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            data-testid="save-review-config-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? '儲存中...' : '儲存配置'}
          </Button>
        </div>
      </header>

      {/* Main Content - 可滾動 */}
      <main className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
        {/* Enable Toggle */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-heading font-bold text-neutral-900">啟用自動審核</h2>
              <p className="text-sm text-neutral-600 mt-1">
                開啟後，Agent 生成的內容會自動經過審核 Agent 檢查
              </p>
            </div>
            <Button
              variant={reviewEnabled ? 'default' : 'outline'}
              onClick={() => setReviewEnabled(!reviewEnabled)}
              className="rounded-full"
              data-testid="toggle-review-enabled-btn"
            >
              {reviewEnabled ? '已啟用' : '停用中'}
            </Button>
          </div>
        </Card>

        {/* Review Agents Warning */}
        {reviewAgents.length === 0 && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">尚未建立審核 Agent</h3>
                <p className="text-sm text-amber-700 mt-1">
                  請先建立至少一個審核 Agent，才能設定審核點。
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/review-agents')}
                  className="mt-2 rounded-full"
                >
                  前往建立審核 Agent
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Checkpoints Section */}
        {reviewEnabled && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-bold text-neutral-900">審核點設定</h2>
                <p className="text-sm text-neutral-600">
                  定義 Agent 在哪些階段需要觸發審核（最多 5 個）
                </p>
              </div>
              <Button
                variant="outline"
                onClick={addCheckpoint}
                disabled={checkpoints.length >= 5 || reviewAgents.length === 0}
                className="rounded-full"
                data-testid="add-checkpoint-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                新增審核點
              </Button>
            </div>

            {checkpoints.length === 0 ? (
              <Card className="p-8 text-center border-dashed">
                <Shield className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-600">尚未設定審核點</p>
                <p className="text-sm text-neutral-500 mt-1">點擊上方按鈕新增第一個審核點</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {checkpoints.map((checkpoint, index) => (
                  <Card 
                    key={checkpoint.id} 
                    className="p-6"
                    data-testid={`checkpoint-card-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-5 h-5 text-neutral-300" />
                        <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-medium">
                          {index + 1}
                        </span>
                        <h3 className="font-medium text-neutral-900">審核點 {index + 1}</h3>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCheckpoint(index)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`remove-checkpoint-${index}-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* Checkpoint Name */}
                      <div>
                        <Label>審核點名稱 *</Label>
                        <Input
                          value={checkpoint.name}
                          onChange={(e) => updateCheckpoint(index, 'name', e.target.value)}
                          placeholder="例：資料收集審核、故事生成審核"
                          data-testid={`checkpoint-name-${index}-input`}
                        />
                      </div>

                      {/* Select Review Agent */}
                      <div>
                        <Label>選擇審核 Agent *</Label>
                        <Select
                          value={checkpoint.review_agent_id}
                          onValueChange={(value) => updateCheckpoint(index, 'review_agent_id', value)}
                        >
                          <SelectTrigger 
                            className="bg-white"
                            data-testid={`checkpoint-review-agent-${index}-select`}
                          >
                            <SelectValue placeholder="選擇一個審核 Agent..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {reviewAgents.map((ra) => (
                              <SelectItem 
                                key={ra.id} 
                                value={ra.id}
                                className="bg-white hover:bg-amber-50"
                              >
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-amber-600" />
                                  <span>{ra.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* 審核標記設定 */}
                      <div className="border-t pt-4 mt-4">
                        <h4 className="text-sm font-medium text-neutral-900 mb-3">🏷️ 審核標記設定</h4>
                        <p className="text-xs text-neutral-600 mb-3">
                          設定 Main Agent 和 Review Agent 使用的特殊標記。您需要在相應的 Agent system prompt 中要求它們使用這些標記。
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Trigger Keyword */}
                          <div>
                            <Label className="text-xs">觸發審核標記</Label>
                            <Input
                              value={checkpoint.trigger_keyword || '[送審]'}
                              onChange={(e) => updateCheckpoint(index, 'trigger_keyword', e.target.value)}
                              placeholder="[送審]"
                              className="text-sm"
                              data-testid={`checkpoint-trigger-${index}-input`}
                            />
                            <p className="text-xs text-neutral-500 mt-1">
                              Main Agent 用此標記觸發審核
                            </p>
                          </div>

                          {/* Pass Keyword */}
                          <div>
                            <Label className="text-xs">審核通過標記</Label>
                            <Input
                              value={checkpoint.pass_keyword || '[通過]'}
                              onChange={(e) => updateCheckpoint(index, 'pass_keyword', e.target.value)}
                              placeholder="[通過]"
                              className="text-sm"
                              data-testid={`checkpoint-pass-${index}-input`}
                            />
                            <p className="text-xs text-neutral-500 mt-1">
                              Review Agent 審核通過時使用
                            </p>
                          </div>

                          {/* Fail Keyword */}
                          <div>
                            <Label className="text-xs">審核失敗標記</Label>
                            <Input
                              value={checkpoint.fail_keyword || '[不通過]'}
                              onChange={(e) => updateCheckpoint(index, 'fail_keyword', e.target.value)}
                              placeholder="[不通過]"
                              className="text-sm"
                              data-testid={`checkpoint-fail-${index}-input`}
                            />
                            <p className="text-xs text-neutral-500 mt-1">
                              Review Agent 審核失敗時使用
                            </p>
                          </div>
                        </div>
                        
                        {/* On Fail Action */}
                        <div className="mt-4">
                          <Label className="text-sm">審核不通過時的處理方式 *</Label>
                          <Select
                            value={checkpoint.on_fail_action || 'ask_user'}
                            onValueChange={(value) => updateCheckpoint(index, 'on_fail_action', value)}
                          >
                            <SelectTrigger className="bg-white mt-2" data-testid={`checkpoint-on-fail-${index}-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="ask_user" className="bg-white hover:bg-amber-50">
                                <div className="flex items-center gap-2">
                                  <span>🙋</span>
                                  <span>返回前端詢問用戶補充資料</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="agent_modify" className="bg-white hover:bg-amber-50">
                                <div className="flex items-center gap-2">
                                  <span>🔄</span>
                                  <span>讓 Agent 自行修改後重新審核</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-neutral-500 mt-1">
                            選擇「詢問用戶」適合需要收集用戶資料的場景；選擇「Agent 修改」適合內容生成的場景
                          </p>
                        </div>

                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-800">
                            <span className="font-medium">⚠️ 重要提醒：</span>
                            您需要在 Main Agent 的 system prompt 中要求它在需要審核時使用「{checkpoint.trigger_keyword || '[送審]'}」標記，
                            並在 Review Agent 的 system prompt 中要求它使用「{checkpoint.pass_keyword || '[通過]'}」或「{checkpoint.fail_keyword || '[不通過]'}」標記來回覆審核結果。
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Help Info */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">運作流程</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700">
                    <li>Main Agent 生成內容後，在內容中包含您設定的「觸發審核標記」(例如 [送審])</li>
                    <li>後端檢測到標記後,停止向前端發送,將內容交給 Review Agent</li>
                    <li>Review Agent 審核內容,並在回覆中使用「通過標記」或「失敗標記」</li>
                    <li>審核通過: Main Agent 接收反饋並生成最終回覆給用戶</li>
                    <li>審核失敗 + ask_user模式: Main Agent 向用戶提問補充資料</li>
                    <li>審核失敗 + agent_modify模式: Main Agent 在後端自行修改後重新審核</li>
                    <li>用戶不會看到 [送審] 標記、原始內容或審核報告,只會看到最終結果</li>
                  </ol>
                  <p className="mt-2 font-medium">⚠️ 重要:</p>
                  <p className="text-blue-700">
                    您需要在 Agent 的 system prompt 中明確要求它們使用這些標記。系統只負責檢測和處理,不會自動添加。
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default AgentReviewConfig;
